import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = process.env.REACT_APP_INTERNAL_ADMIN_SERVER;

export interface ReportAlumniRow {
    student_id: string;
    institute_id: number | null;
    name: string | null;
    program_name: string | null;
    pass_out_year: number | null;
    program_and_year: string;
    campus_name: string | null;
    company_name: string | null;
    layer: 'signal' | 'strategic' | 'activation';
    watchlist_code: string;
    watchlist_label: string;
    outcome: string | null;
    outcome_label: string | null;
    stage: string | null;
    stage_label: string | null;
    // Engagement signals (replaces the legacy A/W/I display). Stored as 'HIGH' | 'MODERATE' | 'LOW'
    // (or null when unscored) — rendered as the first letter H / M / L with a tooltip.
    alma_mater_pride: string | null;
    marketability: string | null;
    vibrancy: string | null;
    evidence_text: string | null;
    is_pitch_ready: boolean;
    reachout_phase: number | null;
    is_manual_override: boolean;
    override_type: string | null;
    override_reason: string | null;
    override_applied_by: string | null;
    override_applied_at: string | null;
    date_added: string | null;
    has_pending_review: boolean;
}

export interface ReportResponse {
    filters_applied: Record<string, any>;
    total_count: number;
    page: number;
    per_page: number;
    total_pages: number;
    alumni: ReportAlumniRow[];
}

// The full set of report filters (mirrors the URL contract / backend query params).
export interface ReportFilters {
    institute_id: string | number;
    filter_institute_id?: string | null;
    layer?: string | null;
    watchlist_code?: string | null;
    outcome?: string | null;
    stage?: string | null;
    program?: string | null;
    batch_year?: string | null;
    alma_mater_pride?: string | null;
    marketability?: string | null;
    vibrancy?: string | null;
    connected?: string | null;
    recognized?: string | null;
    engaged?: string | null;
    pitch_ready?: string | null;
    reachout_phase?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    search?: string | null;
    sort?: string | null;
    page?: string | number | null;
    per_page?: string | number | null;
}

// Strip null/empty values so they don't become "?layer=" noise in the request.
const clean = (f: ReportFilters): Record<string, any> => {
    const out: Record<string, any> = {};
    Object.entries(f).forEach(([k, v]) => {
        if (v != null && v !== '') out[k] = v;
    });
    return out;
};

class WatchlistReportService {
    static getReport(filters: ReportFilters): Promise<ReportResponse> {
        return axiosClient().get(`${BASE}/watchlists/report`, { params: clean(filters) }).then((r) => r.data as ReportResponse);
    }

    // Distinct programs + batch years for the institute/group scope — powers the Program and
    // Batch-year filter dropdowns.
    static getFilterOptions(institute_id: string | number, filter_institute_id?: string | null): Promise<{ programs: string[]; batch_years: number[] }> {
        const params: Record<string, any> = { institute_id };
        if (filter_institute_id) params.filter_institute_id = filter_institute_id;
        return axiosClient().get(`${BASE}/watchlists/report/filter-options`, { params }).then((r) => r.data as { programs: string[]; batch_years: number[] });
    }

    static exportUrl(filters: ReportFilters): string {
        const qs = new URLSearchParams(clean(filters) as Record<string, string>).toString();
        return `${BASE}/watchlists/report/export?${qs}`;
    }

    static exportCSV(filters: ReportFilters): Promise<Blob> {
        return axiosClient().get(`${BASE}/watchlists/report/export`, {
            params: clean(filters),
            responseType: 'blob',
        }).then((r) => r.data as Blob);
    }

    // ── §7.1 pitch readiness ──
    static markPitchReady(body: { institute_id: number; student_id: string; watchlist_code: string; outcome?: string | null; reachout_phase?: number | null; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/report/mark-pitch-ready`, body).then((r) => r.data);
    }
    static unmarkPitchReady(body: { institute_id: number; student_id: string; watchlist_code: string }) {
        return axiosClient().post(`${BASE}/watchlists/report/unmark-pitch-ready`, body).then((r) => r.data);
    }
    static bulkMarkReady(body: { institute_id: number; student_ids: string[]; watchlist_code: string; outcome?: string | null; reachout_phase?: number | null; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/report/bulk-mark-ready`, body).then((r) => r.data);
    }
    // Adjust the reachout phase of an alumni already marked pitch-ready (no unmark/re-mark needed).
    static updatePhase(body: { institute_id: number; student_id: string; watchlist_code: string; reachout_phase: number }) {
        return axiosClient().post(`${BASE}/watchlists/report/update-phase`, body).then((r) => r.data);
    }

    // ── §7.2 overrides ──
    static markDormant(body: { institute_id: number; student_id: string; layer: string; outcome?: string | null; reason?: string }) {
        return axiosClient().post(`${BASE}/watchlists/report/mark-dormant`, body).then((r) => r.data);
    }
    static markActive(body: { institute_id: number; student_id: string; layer: string; outcome?: string | null }) {
        return axiosClient().post(`${BASE}/watchlists/report/mark-active`, body).then((r) => r.data);
    }
    static advanceStage(body: { institute_id: number; student_id: string; outcome: string; target_stage: string; reason?: string }) {
        return axiosClient().post(`${BASE}/watchlists/report/advance-stage`, body).then((r) => r.data);
    }
    static clearOverride(body: { institute_id: number; student_id: string; layer: string; outcome?: string | null }) {
        return axiosClient().post(`${BASE}/watchlists/report/clear-override`, body).then((r) => r.data);
    }
}

export default WatchlistReportService;
